import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import { ReaderSelection, spliceText } from '../../lib/reader/selection';
import { useSuggestSceneWording } from '../../hooks/useBookReader';

interface Props {
  selection: ReaderSelection;
  sceneId: string;
  saving: boolean;
  // Receives the full reworded paragraph (the chosen phrase spliced back into
  // the selected block); the parent rebuilds and saves the scene body.
  onApply: (newParagraph: string) => void;
  // Opens the scene Interview seeded with this passage ("talk about it").
  onTalk: () => void;
  // Shares the selected phrase as a quote (native share sheet / clipboard).
  onShare?: () => void;
  onClose: () => void;
}

/**
 * Bottom sheet for rewording a phrase the reader selected in scene prose. The
 * reader can edit the phrase by hand or ask the AI for drop-in alternatives, or
 * jump into the scene interview to talk it through. Applying splices the chosen
 * wording back into the paragraph and saves. Mirrors the translation reader's
 * WordingSheet.
 */
export default function ScenePhraseSheet({
  selection,
  sceneId,
  saving,
  onApply,
  onTalk,
  onShare,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const suggest = useSuggestSceneWording();
  const [draft, setDraft] = useState(selection.text);
  const [options, setOptions] = useState<string[]>([]);
  const [asked, setAsked] = useState(false);
  const [copied, setCopied] = useState(false);

  // On a new selection: reset the draft and any prior suggestions.
  useEffect(() => {
    setDraft(selection.text);
    setOptions([]);
    setAsked(false);
    setCopied(false);
    suggest.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const askAi = async () => {
    const list = await suggest.mutateAsync({
      sceneId,
      selectedText: selection.text,
      precedingText: selection.base.slice(0, selection.start),
      followingText: selection.base.slice(selection.end),
    });
    setOptions(list);
    setAsked(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(selection.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context / denied) */
    }
  };

  const apply = () => {
    const phrase = draft.trim();
    if (!phrase) return;
    onApply(spliceText(selection.base, selection.start, selection.end, phrase));
  };

  const unchanged = draft.trim() === selection.text.trim();

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <button
        aria-label={t('common.close', 'Close')}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
      />
      <div className="relative max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white px-5 pb-6 pt-3 shadow-2xl dark:bg-gray-800">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-300" />

        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t('bookReader.alternativeWording', 'Alternative wording')}
          </span>
          <div className="-mr-1 flex items-center gap-1">
            <button
              onClick={copy}
              title={t('bookReader.copyQuote', 'Copy quote')}
              className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
            >
              {copied ? `✓ ${t('common.copied', 'Copied')}` : `⧉ ${t('common.copy', 'Copy')}`}
            </button>
            {onShare && (
              <button
                onClick={onShare}
                title={t('bookReader.shareQuote', 'Share quote')}
                className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
              >
                ↗ {t('reader.share', 'Share')}
              </button>
            )}
          </div>
        </div>

        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
          {t('bookReader.editYourself', 'Edit it yourself')}
        </label>
        <TextareaAutosize
          className="w-full resize-none rounded-lg border border-slate-300 p-2 font-serif text-[1.05rem] dark:border-gray-600 dark:bg-gray-900"
          minRows={1}
          maxRows={8}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={apply}
            disabled={saving || unchanged || !draft.trim()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
          >
            {saving ? t('common.applying', 'Applying…') : t('common.apply', 'Apply')}
          </button>
          <button
            onClick={askAi}
            disabled={suggest.isPending}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700"
          >
            {suggest.isPending
              ? t('bookReader.askingAi', 'Asking AI…')
              : `✨ ${t('bookReader.askAi', 'Ask AI')}`}
          </button>
          <button onClick={onClose} className="ml-auto px-2 py-1.5 text-sm text-slate-500">
            {t('common.cancel', 'Cancel')}
          </button>
        </div>

        <button
          onClick={onTalk}
          className="mt-3 flex w-full items-center gap-2 border-t border-slate-100 pt-3 text-left text-sm text-slate-500 hover:text-slate-800 dark:border-gray-700 dark:hover:text-slate-200"
        >
          💬 {t('bookReader.talkAboutThis', 'Talk about this in the interview')}
        </button>

        {suggest.isError && (
          <p className="mt-3 text-sm text-red-600">
            {t('bookReader.suggestError', 'Couldn’t get suggestions. Try again.')}
          </p>
        )}

        {asked && !suggest.isPending && options.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">
            {t(
              'bookReader.noSuggestions',
              'This wording already reads well — no changes suggested.'
            )}
          </p>
        )}

        {options.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t('bookReader.tapSuggestion', 'Tap a suggestion to use it')}
            </div>
            {options.map((opt, i) => (
              <button
                key={i}
                onClick={() => setDraft(opt)}
                className={`block w-full rounded-lg border p-3 text-left font-serif text-[1.05rem] leading-relaxed hover:bg-slate-50 dark:hover:bg-gray-700 ${
                  draft === opt
                    ? 'border-slate-900 bg-slate-50 dark:border-slate-400 dark:bg-gray-700'
                    : 'border-slate-200 dark:border-gray-600'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
